<template>
    <div class="acc-profile-page relative min-h-[calc(100vh-6.5rem)] bg-foxy-background text-slate-900 dark:text-white" :style="themeCssVars">
        <div class="relative z-10 pb-10">
            <div class="mx-auto w-full max-w-7xl px-4 pt-4 sm:px-6 lg:px-8">
                <nav v-if="profile && !loading" class="acc-account-nav scrollbar-hide mb-4" aria-label="Розділи профілю">
                    <router-link class="acc-account-nav-chip" :to="`/u/${profile.username}/overview`">Загальне</router-link>
                    <router-link v-if="!isRestricted" class="acc-account-nav-chip" :to="`/u/${profile.username}/library`">Бібліотека</router-link>
                    <router-link
                        class="acc-account-nav-chip"
                        :class="{ 'router-link-active': isPublicationsRoute }"
                        :to="`/u/${profile.username}/reviews`"
                        >Публікації</router-link
                    >
                    <router-link v-if="!isRestricted" class="acc-account-nav-chip" :to="`/u/${profile.username}/awards`">Нагороди</router-link>
                    <router-link v-if="isOwner" class="acc-account-nav-chip" :to="`/u/${profile.username}/saved`">Збережене</router-link>
                    <router-link v-if="isOwner" class="acc-account-nav-chip" :to="`/u/${profile.username}/drafts`">Чернетки</router-link>
                    <router-link v-if="isOwner" class="acc-account-nav-chip" to="/settings">Налаштування</router-link>
                </nav>

                <div
                    v-if="loading"
                    class="acc-glass rounded-2xl border border-slate-200/70 p-6 text-sm text-slate-600 dark:border-white/10 dark:text-white/70"
                >
                    Завантаження профілю...
                </div>
                <div v-else-if="loadError" class="acc-glass rounded-2xl border border-red-400/30 p-6 text-sm text-red-700 dark:text-red-200">
                    {{ loadError }}
                </div>
                <transition name="acc-fade" mode="out-in">
                    <restricted-profile-overview
                        v-if="!loading && !loadError && isRestricted && $route.name === 'acc.profile.overview'"
                        :profile="profile"
                        :dashboard="dashboard"
                    />
                    <router-view
                        v-else-if="!loading && !loadError"
                        :key="$route.fullPath"
                        :profile="profile"
                        :dashboard="dashboard"
                        :is-owner="isOwner"
                        :is-restricted="isRestricted"
                        :viewer="viewer"
                        :publication-context="{ username: profile.username, generation: profileLoadGeneration, finish: onPublicationMutationFinish }"
                        @profile-updated="onProfileUpdated"
                        @discussion-deleted="onDiscussionDeleted"
                        @quote-deleted="onQuoteDeleted"
                        @fact-deleted="onFactDeleted"
                        @publication-mutation-start="onPublicationMutationStart"
                        @publication-mutation-finish="onPublicationMutationFinish"
                    />
                </transition>
            </div>
        </div>
    </div>
</template>

<script lang="js">
import axios from 'axios';
import RestrictedProfileOverview from './RestrictedProfileOverview.vue';

function getBootstrap() {
    return (window && window.__ACCOUNT_BOOTSTRAP__) || { viewer: null, profileUsername: null };
}

function normalizeUsername(input) {
    if (!input) return null;
    const raw = String(input);
    if (!raw) return null;

    // If server accidentally provided a SPA path (e.g. "u/john/overview"), extract username
    const match = raw.match(/(?:^|\/)u\/([^/]+)(?:\/|$)/i);
    if (match && match[1]) return match[1];

    // If it contains slashes, it's not a username
    if (raw.includes('/')) return null;

    return raw;
}

const restrictedRouteNames = new Set(['acc.profile.overview', 'acc.profile.reviews', 'acc.profile.discussions', 'acc.profile.quotes', 'acc.profile.facts', 'acc.profile.quotes-facts']);

export default {
    name: 'AccountApp',
    components: { RestrictedProfileOverview },
    data() {
        const boot = getBootstrap();
        return {
            viewer: boot.viewer || null,
            profile: null,
            dashboard: null,
            isOwner: false,
            profileLoadGeneration: 0,
            publicationRefreshGeneration: 0,
            publicationMutations: [],
            publicationTotalsStale: false,
            isRestricted: false,
            loading: true,
            loadError: '',
        };
    },
    computed: {
        isPublicationsRoute() {
            return ['acc.profile.reviews', 'acc.profile.discussions', 'acc.profile.quotes', 'acc.profile.facts', 'acc.profile.quotes-facts'].includes(this.$route?.name);
        },
        theme() {
            return (
                this.profile?.theme || {
                    accent: '#A84F1D',
                    secondary: '#C7773A',
                    foreground: { light: '#873B14', dark: '#F3A36F' },
                }
            );
        },
        themeAccent() {
            return this.theme?.accent || '#A84F1D';
        },
        themeSecondary() {
            return this.theme?.secondary || '#C7773A';
        },
        themeCssVars() {
            return {
                '--acc-accent': this.themeAccent,
                '--acc-secondary': this.themeSecondary,
                '--acc-accent-soft': this.theme?.soft || '#F3DDD2',
                '--acc-accent-glow': this.theme?.glow || '#D88A55',
                '--acc-accent-foreground-light': this.theme?.foreground?.light || '#873B14',
                '--acc-accent-foreground-dark': this.theme?.foreground?.dark || '#F3A36F',
            };
        },
    },
    watch: {
        '$route.params.username': {
            immediate: true,
            handler(value) {
                const boot = getBootstrap();
                const fallbackUsername = normalizeUsername(boot.profileUsername) || (boot.viewer && boot.viewer.username) || null;
                const username = normalizeUsername(value) || fallbackUsername;

                if (!username) {
                    this.loading = false;
                    this.loadError = 'Не вдалося визначити користувача профілю.';
                    return;
                }

                this.fetchProfile(username);
            },
        },
        '$route.name'() {
            if (!this.loading && this.profile) this.enforceResolvedProfileRoute();
        },
    },
    methods: {
        isCurrentPublicationSubject(context) {
            return this.isOwner && Number.isInteger(context?.generation) && context.generation > 0
                && context?.username === this.profile?.username
                && context?.generation === this.profileLoadGeneration;
        },
        applyPublicationTotal(key, count, context) {
            if (!this.isCurrentPublicationSubject(context) || !this.dashboard?.stats) return;
            if ((this.publicationMutations || []).length || !Number.isInteger(count) || count < 0) {
                this.publicationTotalsStale = true;
                return;
            }
            this.dashboard = { ...this.dashboard, stats: { ...this.dashboard.stats, [key]: count } };
        },
        onFactDeleted(context) { this.applyPublicationTotal('facts_count', context.factsCount, context); },
        onQuoteDeleted(context) { this.applyPublicationTotal('quotes_count', context.quotesCount, context); },
        onDiscussionDeleted(context) { this.applyPublicationTotal('discussions_count', context.discussionsCount, context); },
        onPublicationMutationStart(context) {
            if (!this.isCurrentPublicationSubject(context)) return;
            this.publicationRefreshGeneration += 1;
            this.publicationTotalsStale = true;
            this.publicationMutations = [...this.publicationMutations, context];
        },
        async onPublicationMutationFinish(context) {
            if (!this.isCurrentPublicationSubject(context)) return;
            this.publicationMutations = this.publicationMutations.filter(item => item !== context);
            if (!this.publicationMutations.length) await this.refreshPublicationTotals(context);
        },
        async refreshPublicationTotals(context) {
            if (!this.isCurrentPublicationSubject(context)) return;
            const request = ++this.publicationRefreshGeneration;
            try {
                const { data } = await axios.get(`/api/account/profile/${encodeURIComponent(context.username)}`);
                if (request !== this.publicationRefreshGeneration || !this.isCurrentPublicationSubject(context)
                    || data?.profile?.username !== context.username || this.publicationMutations.length) return;
                const keys = ['reviews_count', 'discussions_count', 'quotes_count', 'facts_count'];
                if (!keys.every(key => Number.isInteger(data?.dashboard?.stats?.[key]) && data.dashboard.stats[key] >= 0)) return;
                const stats = { ...this.dashboard.stats };
                keys.forEach(key => { stats[key] = data.dashboard.stats[key]; });
                this.dashboard = { ...this.dashboard, stats };
                this.publicationTotalsStale = false;
            } catch (_) {
                // Keep the last known totals stale; never invent zero or a local decrement.
            }
        },
        async fetchProfile(username) {
            const generation = (this.profileLoadGeneration || 0) + 1;
            this.profileLoadGeneration = generation;
            this.publicationRefreshGeneration += 1;
            this.publicationMutations = [];
            this.publicationTotalsStale = false;
            this.loading = true;
            this.isOwner = false;
            this.profile = null;
            this.dashboard = null;
            this.loadError = '';
            this.isRestricted = false;

            try {
                const response = await axios.get(`/api/account/profile/${username}`);
                if (generation !== this.profileLoadGeneration) return;
                this.profile = response.data.profile;
                this.dashboard = response.data.dashboard || null;
                this.isOwner = Boolean(response.data.isOwner);
                this.isRestricted = Boolean(response.data.isRestricted);
                await this.enforceResolvedProfileRoute();
            } catch (error) {
                if (generation !== this.profileLoadGeneration) return;
                this.loadError = error?.response?.data?.message || 'Помилка завантаження профілю.';
            } finally {
                if (generation === this.profileLoadGeneration) this.loading = false;
            }
        },
        async enforceResolvedProfileRoute() {
            const route = this.$route;
            const ownerOnlyRoute = Boolean(route?.meta?.ownerOnly);
            const ownerOnlyRedirectRequired = ownerOnlyRoute && !this.isOwner;
            const restrictedRedirectRequired = this.isRestricted && !restrictedRouteNames.has(route?.name);

            if (!ownerOnlyRedirectRequired && !restrictedRedirectRequired) return false;

            const username = this.profile?.username || normalizeUsername(route?.params?.username);
            if (!username) return false;

            await this.$router.replace({
                name: 'acc.profile.overview',
                params: { username },
            });

            return true;
        },
        onProfileUpdated(nextProfile) {
            this.profile = {
                ...this.profile,
                ...nextProfile,
            };

            const routeUsername = this.$route?.params?.username;
            if (routeUsername && nextProfile?.username && routeUsername !== nextProfile.username) {
                this.$router.replace({ name: 'acc.profile.overview', params: { username: nextProfile.username } });
            }
        },
    },
};
</script>

<style scoped>
.acc-fade-enter-active,
.acc-fade-leave-active {
    transition:
        opacity 160ms ease,
        transform 160ms ease;
}
.acc-fade-enter,
.acc-fade-leave-to {
    opacity: 0;
    transform: translateY(6px);
}
</style>
